import React from 'react'
import styles from './PanelWidgetCard.module.css'
import { PivotTableView } from './PivotTableView'
import { getLocalizedDataset, summarizeReportScope } from '../utils/reportLabels'
import { findMergedLotNames } from '../utils/reportLocationColumns'
import type { ReportDefinitionV1 } from '../utils/reportStorage'
import type { PivotWidgetV1 } from '../utils/dashboardStorage'
import { buildPivotTable, formatPivotValue } from '../utils/pivot'
import {
  bucketRowsForWidget,
  buildBarChartSvg,
  buildLineChartSvg,
  buildMultiSeriesChartSvg,
  CHART_MAX_ITEMS,
  CHART_MAX_SERIES,
  CHART_SERIES_COLORS,
  resolvePivotOrderField,
  sortChartItemsChronologically,
  type ReportRunState
} from '../utils/panelWidgetRendering'
import { useT } from '../i18n'

/**
 * Renders one Panel widget (pivot table + optional chart) — shared by
 * NewDashboard.tsx (Panel, full editing, draggable) and Dashboard.tsx
 * (/home's pinned-widget preview, read-only) so a widget looks and computes
 * identically in both places instead of a second duplicate implementation.
 */
export const PanelWidgetCard = (props: {
  widget: PivotWidgetV1
  report: ReportDefinitionV1 | undefined
  runState: ReportRunState | undefined
  /** Action buttons (edit/delete/pin/etc.) — omit for a read-only card. */
  actions?: React.ReactNode
  /** Applies the drag-handle class to the title block (Panel only). */
  draggable?: boolean
}): React.ReactNode => {
  const { widget, report, runState, actions, draggable } = props
  const t = useT()

  const dataset = report ? getLocalizedDataset(t, report.datasetKey) : null
  const columnLabel = (fieldKey: string): string => (
    dataset?.columns.find((column) => column.key === fieldKey)?.label ?? fieldKey
  )

  let pivot = null
  let pivotError: string | null = null
  if (runState && !runState.loading && !runState.error) {
    try {
      pivot = buildPivotTable(bucketRowsForWidget(runState.rows, widget), {
        rowField: widget.rowField,
        columnField: widget.columnField,
        aggregation: widget.aggregation,
        valueField: widget.valueField,
        orderField: resolvePivotOrderField(widget),
        entityField: widget.entityField,
        denominatorField: widget.denominatorField,
        weightField: widget.weightField,
        ratioMultiplier: widget.ratioMultiplier,
        maxRowKeys: 100,
        maxColumnKeys: 24,
        // Date-bucketed rows sort chronologically, and the chart shows the
        // most recent buckets — so truncation must drop the oldest, not
        // the newest (matters once `day` grouping exceeds 100 buckets).
        keepLatestRowKeys: Boolean(widget.rowDateGroup)
      })
    } catch (error) {
      pivotError = error instanceof Error ? error.message : String(error)
    }
  }

  // The newer aggregations report an empty bucket as NaN (not a real
  // zero) — leave it off the chart rather than plotting a false 0.
  const chartItems = pivot
    ? pivot.rowKeys
      .map((key, index) => ({
        label: key,
        value: formatPivotValue(pivot!.rowTotals[index], widget.aggregation)
      }))
      .filter((item) => Number.isFinite(item.value))
    : []

  const chartItemsOrdered = widget.rowDateGroup
    ? sortChartItemsChronologically(chartItems)
    : chartItems.slice().sort((a, b) => b.value - a.value)

  // v2.2 findings.md #47: one series per column key (e.g. per lot). Rows
  // keep the pivot's own key order (chronological for date labels);
  // series keep the pivot's sorted column order, so a lot keeps its color
  // regardless of its values.
  const isMultiSeries = Boolean(pivot && widget.chartSeries === 'byColumn' && widget.columnField && pivot.columnKeys.length > 0)
  const multiSeries = isMultiSeries && pivot
    ? pivot.columnKeys.map((name, columnIndex) => ({
      name,
      values: pivot!.rowKeys.map((_, rowIndex) => formatPivotValue(pivot!.cells[rowIndex][columnIndex], widget.aggregation))
    }))
    : []
  // Only series with data inside the plotted window get a line, a legend
  // entry and a palette slot — an old lot with nothing in the last
  // `CHART_MAX_ITEMS` buckets would otherwise sit in the legend with no
  // visible mark (and burn one of the eight hues).
  const rowCount = pivot?.rowKeys.length ?? 0
  const windowStart = widget.rowDateGroup ? Math.max(0, rowCount - CHART_MAX_ITEMS) : 0
  const windowEnd = widget.rowDateGroup ? rowCount : Math.min(rowCount, CHART_MAX_ITEMS)
  const seriesWithData = multiSeries.filter((series) => (
    series.values.slice(windowStart, windowEnd).some((value) => Number.isFinite(value))
  ))
  const shownSeries = seriesWithData.slice(0, CHART_MAX_SERIES)

  const aggLabel = t(`dashboardNew.aggregation.${widget.aggregation}`)

  // findings.md #67: grouping by the plain lot *name* merges same-name lots
  // that live in different units — say so, and point to "Lote · Unidad".
  const groupsByLotName = [widget.rowField, widget.columnField, widget.entityField]
    .some((field) => field === 'lot' || field === 'lotKey')
  const mergedLots = groupsByLotName && runState && !runState.loading && !runState.error
    ? findMergedLotNames(runState.rows)
    : []

  const yAxisLabel = widget.aggregation === 'count'
    ? aggLabel
    : (widget.valueField ? `${aggLabel}(${columnLabel(widget.valueField)})` : aggLabel)

  return (
    <div className={styles.widgetCard}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div className={draggable ? styles.widgetDragHandle : undefined} title={draggable ? t('dashboardNew.widgets.dragHandleHint') : undefined}>
          <div style={{ fontWeight: 800 }}>{widget.title}</div>
          <div className="lead">
            {report ? report.name : t('dashboardNew.widgets.missingReport')} · {dataset?.label ?? report?.datasetKey ?? ''}
          </div>
          <div className="lead" title={t('reports.help.filterHint')}>
            {t('dashboardNew.widgets.filterPrefix')}: {summarizeReportScope(t, report?.scope)}
          </div>
          <div className="lead" title={t('dashboardNew.help.breakdownHint')}>
            {t('dashboardNew.widgets.breakdownPrefix')}: {t('dashboardNew.widgets.rowsPrefix')} {columnLabel(widget.rowField)}
            {widget.columnField ? ` · ${t('dashboardNew.widgets.colsPrefix')} ${columnLabel(widget.columnField)}` : ''}
            {widget.aggregation ? ` · ${aggLabel}` : ''}
            {widget.valueField ? `(${columnLabel(widget.valueField)})` : ''}
          </div>
          {mergedLots.length > 0 && (
            <div
              data-testid="panel-widget-merged-lots-warning"
              style={{ marginTop: 4, padding: '4px 8px', borderRadius: 6, backgroundColor: '#fffbeb', color: '#92400e', fontSize: 12, fontWeight: 600 }}
            >
              {mergedLots.map((entry) => t('dashboardNew.widgets.mergedLotsWarning', {
                lot: entry.lot,
                count: entry.lotUnits.length,
                units: entry.lotUnits.join(', ')
              })).join(' ')}
            </div>
          )}
        </div>

        {actions && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {actions}
          </div>
        )}
      </div>

      {!report && (
        <div className="lead" style={{ marginTop: 10, color: '#b91c1c' }}>
          {t('dashboardNew.widgets.missingReportWarning')}
        </div>
      )}

      {report && runState?.loading && (
        <div className="lead" style={{ marginTop: 10 }}>{t('dashboardNew.status.loadingReportRows')}</div>
      )}

      {report && runState?.error && (
        <div className="lead" style={{ marginTop: 10, color: '#b91c1c' }}>
          {t('dashboardNew.error.runFailed', { message: runState.error })}
        </div>
      )}

      {pivotError && (
        <div className="lead" style={{ marginTop: 10, color: '#b91c1c' }}>
          {t('dashboardNew.error.pivotFailed', { message: pivotError })}
        </div>
      )}

      {pivot && (
        <>
          {(pivot.truncated.rows || pivot.truncated.columns) && (
            <div className="lead" style={{ marginTop: 10 }}>
              {t('dashboardNew.status.showingUpTo', {
                rowCount: String(pivot.rowKeys.length),
                colCount: String(pivot.columnKeys.length || 1)
              })}
            </div>
          )}

          {widget.showChart && isMultiSeries && pivot && pivot.rowKeys.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>{t('dashboardNew.chart.titleBySeries', { field: columnLabel(widget.columnField ?? '') })}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px', marginBottom: 6, fontSize: 12, color: '#374151' }}>
                {shownSeries.map((series, index) => (
                  <span key={series.name} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <span aria-hidden="true" style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: CHART_SERIES_COLORS[index] }} />
                    {series.name}
                  </span>
                ))}
              </div>
              <div
                style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: 12,
                  padding: 10,
                  overflowX: 'auto',
                  WebkitOverflowScrolling: 'touch'
                }}
                dangerouslySetInnerHTML={{
                  __html: buildMultiSeriesChartSvg({
                    xLabels: pivot.rowKeys,
                    series: shownSeries,
                    chartType: widget.chartType === 'line' ? 'line' : 'bar',
                    yAxisLabel,
                    ariaLabel: t('dashboardNew.chart.ariaLabel'),
                    trimFromEnd: Boolean(widget.rowDateGroup)
                  })
                }}
              />
              {(seriesWithData.length > CHART_MAX_SERIES || pivot.rowKeys.length > CHART_MAX_ITEMS) && (
                <div className="lead" style={{ marginTop: 6 }}>
                  {t('dashboardNew.chart.truncatedSeries', {
                    shownSeries: String(shownSeries.length),
                    totalSeries: String(seriesWithData.length),
                    shown: String(Math.min(CHART_MAX_ITEMS, pivot.rowKeys.length)),
                    total: String(pivot.rowKeys.length)
                  })}
                </div>
              )}
            </div>
          )}

          {widget.showChart && !isMultiSeries && chartItems.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>{t('dashboardNew.chart.title')}</div>
              <div
                style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: 12,
                  padding: 10,
                  overflowX: 'auto',
                  WebkitOverflowScrolling: 'touch'
                }}
                dangerouslySetInnerHTML={{
                  __html: (widget.chartType === 'line' ? buildLineChartSvg : buildBarChartSvg)({
                    items: chartItemsOrdered,
                    yAxisLabel,
                    ariaLabel: t('dashboardNew.chart.ariaLabel'),
                    trimFromEnd: Boolean(widget.rowDateGroup)
                  })
                }}
              />
              {chartItemsOrdered.length > CHART_MAX_ITEMS && (
                <div className="lead" style={{ marginTop: 6 }}>
                  {t('dashboardNew.chart.truncated', {
                    shown: String(CHART_MAX_ITEMS),
                    total: String(chartItemsOrdered.length)
                  })}
                </div>
              )}
            </div>
          )}

          <PivotTableView
            pivot={pivot}
            rowField={columnLabel(widget.rowField)}
            aggregation={widget.aggregation}
            totalLabel={t('dashboardNew.pivot.total')}
            grandTotalLabel={t('dashboardNew.pivot.grandTotal')}
          />
        </>
      )}
    </div>
  )
}
